import React, { useState } from "react";
import { useSelector } from 'react-redux';
import Modal from '@mui/material/Modal';
import { floral } from '../language';

const LOCAL_URL = ['http://localhost:3001', 'http://127.0.0.1:3001', 'http://192.168.50.185:3001'];
const LOCAL_TEST = LOCAL_URL.includes(window.location.origin);

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  weddingDate: '',
  venue: '',
  guestCount: '',
  floralBudget: '',
  servicesNeeded: '',
  message: '',
};

function formatPhoneNumber(rawValue) {
  const digits = rawValue.replace(/\D/g, '').slice(0, 10);
  const areaCode = digits.slice(0, 3);
  const prefix = digits.slice(3, 6);
  const lineNumber = digits.slice(6, 10);
  if (digits.length > 6) {
    return `${areaCode}-${prefix}-${lineNumber}`;
  }
  if (digits.length > 3) {
    return `${areaCode}-${prefix}`;
  }
  return areaCode;
}

function InquiryForm(props) {
  const languageEN = useSelector((state) => {
    return state.app.languageEN;
  });
  const language = languageEN ? 'en' : 'ch';

  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  function handleChange(field, value) {
    setFormValues((values) => {
      return { ...values, [field]: value };
    });
  }

  function isFormValid() {
    const allFieldsFilled = Object.keys(EMPTY_FORM).every((field) => {
      return String(formValues[field]).trim().length > 0;
    });
    const phoneDigits = formValues.phone.replace(/\D/g, '');
    return allFieldsFilled && phoneDigits.length === 10;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!isFormValid()) {
      setStatus({ success: false, message: floral.inquiry.validationError[language] });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    let baseUrl = "";
    if (LOCAL_TEST) {
      const currentUrl = window.location.origin.slice(0, -5);
      baseUrl = `${currentUrl}:5000`;
    }

    try {
      const response = await fetch(`${baseUrl}/api/submitInquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formValues,
          guestCount: Number(formValues.guestCount),
        }),
      });
      const result = await response.json();
      if (result.success) {
        setFormValues(EMPTY_FORM);
        setShowSuccessModal(true);
      } else {
        setStatus({ success: false, message: floral.inquiry.errorMessage[language] });
      }
    } catch (err) {
      setStatus({ success: false, message: floral.inquiry.errorMessage[language] });
    }

    setSubmitting(false);
  }

  return (
    <div id="contact" className="lf-bg-tint lf-section-anchor">
      <div className="lf-editorial lf-section py-5">
        <div className="row">
          <div className="col-lg-5 lf-reveal">
            <p className="lf-eyebrow">{floral.inquiry.eyebrow[language]}</p>
            <h2 className="lf-section-heading" style={{ fontSize: '2.35rem', lineHeight: 1.22, letterSpacing: '-0.04em' }}>
              {floral.inquiry.title[language]}
            </h2>
            <p className="mt-3" style={{ color: 'var(--lf-muted)', lineHeight: 1.6, maxWidth: '26rem' }}>
              {floral.inquiry.description[language]}
            </p>
          </div>
          <div className="col-lg-7 lf-reveal mt-4 mt-lg-0">
            <form onSubmit={handleSubmit} noValidate>
              <div className="row">
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.name[language]}</label>
                  <input
                    className="lf-form-control"
                    type="text"
                    autoComplete="name"
                    value={formValues.name}
                    onChange={(e) => {
                      handleChange('name', e.target.value);
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.email[language]}</label>
                  <input
                    className="lf-form-control"
                    type="email"
                    autoComplete="email"
                    value={formValues.email}
                    onChange={(e) => {
                      handleChange('email', e.target.value);
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.phone[language]}</label>
                  <input
                    className="lf-form-control"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="XXX-XXX-XXXX"
                    maxLength={12}
                    value={formValues.phone}
                    onChange={(e) => {
                      handleChange('phone', formatPhoneNumber(e.target.value));
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.weddingDate[language]}</label>
                  <input
                    className="lf-form-control"
                    type="date"
                    value={formValues.weddingDate}
                    onChange={(e) => {
                      handleChange('weddingDate', e.target.value);
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.venue[language]}</label>
                  <input
                    className="lf-form-control"
                    type="text"
                    value={formValues.venue}
                    onChange={(e) => {
                      handleChange('venue', e.target.value);
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.guestCount[language]}</label>
                  <input
                    className="lf-form-control"
                    type="number"
                    min="1"
                    inputMode="numeric"
                    value={formValues.guestCount}
                    onChange={(e) => {
                      handleChange('guestCount', e.target.value);
                    }}
                  />
                </div>
                <div className="col-sm-6 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.floralBudget[language]}</label>
                  <select
                    className="lf-form-control"
                    value={formValues.floralBudget}
                    onChange={(e) => {
                      handleChange('floralBudget', e.target.value);
                    }}
                  >
                    <option value="">{floral.inquiry.floralBudgetPlaceholder[language]}</option>
                    {floral.inquiry.floralBudgetOptions.map((option) => {
                      return (
                        <option key={option.en} value={option.en}>{option[language]}</option>
                      );
                    })}
                  </select>
                </div>
                <div className="col-12 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.servicesNeeded[language]}</label>
                  <input
                    className="lf-form-control"
                    type="text"
                    value={formValues.servicesNeeded}
                    onChange={(e) => {
                      handleChange('servicesNeeded', e.target.value);
                    }}
                  />
                </div>
                <div className="col-12 mb-3">
                  <label className="d-block" style={{ fontSize: '0.85rem', fontWeight: 600 }}>{floral.inquiry.message[language]}</label>
                  <textarea
                    className="lf-form-control"
                    style={{ minHeight: '110px', resize: 'vertical' }}
                    value={formValues.message}
                    onChange={(e) => {
                      handleChange('message', e.target.value);
                    }}
                  />
                </div>
              </div>
              {status && (
                <p className="mt-2" style={{ fontSize: '0.9rem', color: '#8b3c32' }}>
                  {status.message}
                </p>
              )}
              <button
                type="submit"
                className="lf-btn-primary mt-3"
                disabled={submitting}
                style={{ opacity: submitting ? 0.65 : 1, color: '#fff' }}
              >
                {submitting ? floral.inquiry.sending[language] : floral.inquiry.submit[language]}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Modal
        open={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
        }}
        container={() => {
          return document.getElementsByClassName('lynsis-home')[0];
        }}
      >
        <div
          className="lf-modal-card"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <h3 className="lf-section-heading" style={{ fontSize: '1.6rem' }}>
            {floral.inquiry.successTitle[language]}
          </h3>
          <p className="mt-3" style={{ color: 'var(--lf-muted)', lineHeight: 1.6 }}>
            {floral.inquiry.successMessage[language]}
          </p>
          <button
            type="button"
            className="lf-btn-primary mt-4"
            style={{ color: '#fff' }}
            onClick={() => {
              setShowSuccessModal(false);
            }}
          >
            {floral.inquiry.close[language]}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export { InquiryForm };
